/**
 * RTWO Cryptography & Identity Engine — Null Key Provider
 */

import type {
  CryptoAlgorithm,
  EllipticCurve,
  KeyPairId,
  MessageHashHex,
  PublicKeyHex,
  RTWOKeyPair,
} from '../domain-models';
import type { KeyPairGenerationRequest } from '../service-interfaces';
import type {
  AddressProviderResult,
  IKeyProvider,
  KeyPairProviderResult,
  SignatureProviderResult,
} from './key-provider-interface';

export class NullKeyProvider implements IKeyProvider {
  readonly providerId = 'null-key-provider';
  readonly providerName = 'Null Key Provider';
  readonly providerType = 'not_configured' as const;

  async connect(): Promise<void> {}
  async disconnect(): Promise<void> {}

  getStatus() {
    return 'not_configured' as const;
  }

  isReady(): boolean {
    return false;
  }

  async generateKeyPair(_request: KeyPairGenerationRequest): Promise<KeyPairProviderResult | null> {
    return null;
  }

  async deriveAddress(
    _keyPairId: KeyPairId,
    _addressType: string,
    _derivationPath?: string
  ): Promise<AddressProviderResult | null> {
    return null;
  }

  async signMessage(
    _keyPairId: KeyPairId,
    _messageHash: MessageHashHex,
    _algorithm: CryptoAlgorithm
  ): Promise<SignatureProviderResult | null> {
    return null;
  }

  async verifySignature(
    _messageHash: MessageHashHex,
    _signature: string,
    _publicKey: PublicKeyHex,
    _algorithm: CryptoAlgorithm
  ): Promise<boolean> {
    return false;
  }

  async validatePublicKey(
    _publicKey: PublicKeyHex,
    _algorithm: CryptoAlgorithm,
    _curve: EllipticCurve
  ): Promise<boolean> {
    return false;
  }

  async rotateKey(_keyPairId: KeyPairId): Promise<KeyPairProviderResult | null> {
    return null;
  }

  async revokeKey(_keyPairId: KeyPairId): Promise<boolean> {
    return false;
  }

  async getKeyPair(_keyPairId: KeyPairId): Promise<RTWOKeyPair | null> {
    return null;
  }

  getDescriptor() {
    return {
      providerId: this.providerId,
      providerName: this.providerName,
      providerType: this.providerType,
      status: 'not_configured' as const,
      isConfigured: false,
      configuredAt: null,
      description:
        'Default null key provider. No cryptographic operations available until a real Key Provider is configured.',
    };
  }
}
